import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useAuth } from "react-oidc-context";
import { KeyRound, Loader2, Save } from "lucide-react";
import { ROLE_LABELS, type UserDto } from "@ambulance/shared";
import { useApi } from "../api";
import { useMe } from "../hooks/useMe";
import { btnPrimary, ErrorBox, Field, inputCls, inputDisabledCls, Spinner } from "../components/ui";

export function ProfilePage() {
  const { me, isLoading } = useMe();
  const auth = useAuth();
  const api = useApi();
  const qc = useQueryClient();
  const save = useMutation({
    mutationFn: (d: { name: string; badgeNo: string }) => api.patch<UserDto>("/api/v1/me", d),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["me"] }),
  });

  const [name, setName] = useState("");
  const [badgeNo, setBadgeNo] = useState("");
  const [msg, setMsg] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    if (me) {
      setName(me.name ?? "");
      setBadgeNo(me.badgeNo ?? "");
    }
  }, [me]);

  if (isLoading) return <Spinner />;
  if (!me) return null;
  const isParamedic = me.role === "paramedic";

  async function saveProfile(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setErr(null);
    try {
      await save.mutateAsync({ name, badgeNo });
      setMsg("Saved");
      setTimeout(() => setMsg(null), 2500);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  /** Password changes happen on the identity server (it verifies the current password itself). */
  function changePassword() {
    void auth.signinRedirect({ extraQueryParams: { kc_action: "UPDATE_PASSWORD" }, state: { returnTo: "/profile" } });
  }

  return (
    <div className="max-w-2xl">
      <h1 className="text-xl sm:text-2xl font-bold text-slate-900 mb-4 sm:mb-6">My Profile</h1>

      <section className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6 mb-4 sm:mb-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-4">Account information</h2>
        <form onSubmit={saveProfile} className="space-y-4">
          <Field label="Email"><input value={me.email} disabled className={inputDisabledCls} /></Field>
          <Field label="Role"><input value={ROLE_LABELS[me.role]} disabled className={inputDisabledCls} /></Field>
          <Field label="Full name"><input value={name} onChange={(e) => setName(e.target.value)} disabled={isParamedic} className={isParamedic ? inputDisabledCls : inputCls} /></Field>
          <Field label="Badge No"><input value={badgeNo} onChange={(e) => setBadgeNo(e.target.value)} disabled={isParamedic} className={isParamedic ? inputDisabledCls : inputCls} /></Field>
          {me.hospitals.length > 0 && (
            <Field label="Assigned hospitals">
              <div className="flex flex-wrap gap-1.5">
                {me.hospitals.map((h) => <span key={h.id} className="text-xs px-2 py-1 rounded bg-slate-100 text-slate-700">{h.name}</span>)}
              </div>
            </Field>
          )}
          {err && <ErrorBox>{err}</ErrorBox>}
          {!isParamedic && (
            <div className="flex items-center gap-3">
              <button type="submit" disabled={save.isPending} className={btnPrimary}>
                {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                Save changes
              </button>
              {msg && <span className="text-sm text-emerald-600">{msg}</span>}
            </div>
          )}
        </form>
      </section>

      <section className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-slate-900 mb-2 inline-flex items-center gap-2"><KeyRound className="w-5 h-5" /> Change password</h2>
        <p className="text-sm text-slate-600 mb-4">You will be asked for your current password, then a new one that meets the policy (8+ characters, upper and lower case, a number and a special character).</p>
        <button type="button" onClick={changePassword} className="rounded-lg bg-slate-900 hover:bg-slate-800 text-white font-semibold px-4 py-2 inline-flex items-center gap-2">
          <KeyRound className="w-4 h-4" /> Change password
        </button>
      </section>
    </div>
  );
}
